import { Calendar } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { Webinar } from '@domain/models/webinar'
import { stripMarkdownToPlainText } from '@presentation/helpers/markdown'
import {
  extractYouTubeVideoId,
  formatWebinarDate,
  formatWebinarDateTime,
  isUpcomingWebinar,
} from '@presentation/helpers/webinar'

interface WebinarCardProps {
  webinar: Webinar
  showVideoPreview?: boolean
}

export default function WebinarCard({
  webinar,
  showVideoPreview = true,
}: WebinarCardProps) {
  const webinarPath = `/webinars/${webinar.uid}`
  const isUpcoming = isUpcomingWebinar(webinar.webinarDate)
  const videoId = extractYouTubeVideoId(webinar.youtubeUrl)
  const coverImageUrl = webinar.coverImageUrl?.trim() ?? ''
  const hasCoverImage = coverImageUrl.length > 0
  const previewThumbnailUrl = showVideoPreview && videoId
    ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
    : ''
  const hasPreviewThumbnail = previewThumbnailUrl.length > 0
  const summaryPreview = webinar.summary
    ? stripMarkdownToPlainText(webinar.summary)
    : ''
  const dateTimeLabel = formatWebinarDateTime(
    webinar.webinarDate,
    webinar.webinarTime,
    webinar.timeZone,
  )

  return (
    <article
      className="block overflow-hidden rounded-xl bg-card-surface no-underline shadow-lg shadow-black/20 ring-1 ring-black/5 transition-[transform,box-shadow] duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-black/30"
    >
      {hasCoverImage ? (
        <div className="overflow-hidden rounded-t-xl bg-border-light">
          <img
            src={coverImageUrl}
            alt={webinar.title}
            className="h-40 w-full object-cover"
            loading="lazy"
          />
        </div>
      ) : hasPreviewThumbnail ? (
        <div className="overflow-hidden rounded-t-xl bg-border-light">
          <img
            src={previewThumbnailUrl}
            alt={`${webinar.title} preview`}
            className="h-40 w-full object-cover"
            loading="lazy"
          />
        </div>
      ) : showVideoPreview && !isUpcoming ? (
        <div className="flex h-40 items-center justify-center bg-border-light px-4 text-center text-sm font-semibold text-text-muted">
          Webinar recording
        </div>
      ) : null}

      <div className="p-4">
        <div
          className={`mb-2 flex items-start gap-2 font-semibold text-primary ${
            isUpcoming ? 'text-sm sm:text-base' : 'text-xs'
          }`}
        >
          <Calendar className={`${isUpcoming ? 'h-4 w-4' : 'h-3.5 w-3.5'} mt-0.5`} aria-hidden />
          <time>{isUpcoming ? dateTimeLabel : formatWebinarDate(webinar.webinarDate)}</time>
        </div>

        <Link to={webinarPath} className="no-underline">
          <h3 className="text-lg font-bold text-text-primary hover:text-primary">{webinar.title}</h3>
        </Link>

        {summaryPreview && (
          <p className="mt-2 line-clamp-3 text-sm text-text-primary">
            {summaryPreview}
          </p>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <Link
            to={webinarPath}
            aria-label={`${isUpcoming ? 'Register and submit your question for' : 'Watch'} ${webinar.title}`}
            className="inline-flex max-w-full rounded-full bg-blue-light px-2.5 py-0.5 text-center text-xs font-bold leading-tight text-blue no-underline hover:bg-blue/10"
          >
            {isUpcoming ? 'Register and Submit Your Question' : 'Watch it Now'}
          </Link>
        </div>
      </div>
    </article>
  )
}
